import { useRef, useState } from 'react';
import { Camera, FileText } from 'lucide-react';
import { requirementsApi } from '../api/requirements';
import type { CompletionRow, ReturnedForm } from '../api/requirements';
import { ValidationFailure } from '../api/events';
import { Button } from '../ui/Button';
import { CheckboxField, TextField } from '../ui/Field';
import { Overlay } from '../ui/Overlay';
import type { LightboxItem } from './PhotoLightbox';

/** Names drawn in the leader's picker before the rest go behind the search box. */
const DRAWN_SUBJECTS = 8;

/**
 * A returned slip as the lightbox wants it. The caption is what the viewer reads under the
 * photograph and what a screen reader is given as the alt text, so it says whose form this is
 * rather than repeating the file name.
 */
export function asLightboxItem(form: ReturnedForm): LightboxItem & { form: ReturnedForm } {
  return {
    id: form.storedFileId,
    url: form.url,
    caption: `${form.requirementTitle} — ${form.subjects.map((s) => s.displayName).join(' and ')}`,
    addedByDisplayName: form.enteredByName,
    capturedAt: form.completedAt,
    canRemove: form.canWithdraw,
    form,
  };
}

/**
 * The strip of slips returned against one ask, and the control that adds another — TROOP-127.
 *
 * **A thumbnail, not a tick.** §7's evidence tiers exist because a leader glancing at an all-green
 * grid before a whitewater trip is making a safety decision, and half those greens can be parents
 * misremembering which form they filled in. A returned `Document` is the one tier that is actually
 * an artifact, so the screen shows the artifact: the photograph itself, openable, rather than a
 * word claiming one exists.
 *
 * **One form can name several children.** Parents of siblings fill in one slip and write both
 * names on it, so the picker takes more than one person and the card that comes back names them
 * all. The server refuses a set no single person answers for, which is what stops this being a way
 * to hand one family another family's form.
 *
 * **A leader enters the paper they were handed.** §7's on-behalf path, and the reason it matters is
 * adoption rather than convenience — without it the grid is wrong for exactly the families least
 * likely to use the app, which is the problem the whole product is fighting. The names come from
 * the completion grid the leader can already see, so this adds no second roster read and no second
 * answer to who may be looked up.
 */
export function ReturnedForms({
  unitId,
  eventId,
  requirementId,
  forms,
  household,
  canEnterForOthers,
  busy,
  onOpen,
  onChanged,
}: {
  /** Undefined on `/family` — that absence picks the guardian's door (rule 13b). */
  unitId?: string;
  eventId: string;
  requirementId: string;
  /** The slips already returned against this ask, of the ones the caller may see. */
  forms: ReturnedForm[];
  /** Everybody this caller answers for: themselves, and their own children. */
  household: { personId: string; displayName: string }[];
  /**
   * Whether to offer "somebody else's". An adult officer only — the grid this reads is
   * `RequirementVisibilityPolicy`'s answer, so a youth officer gets a 403 and the control would
   * be a promise the server refuses.
   */
  canEnterForOthers: boolean;
  busy: boolean;
  /** Opens the mini gallery at this slip, over every slip on the event. */
  onOpen: (form: ReturnedForm) => void;
  onChanged: () => void | Promise<void>;
}) {
  const [picked, setPicked] = useState<File | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [others, setOthers] = useState<CompletionRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);

  function close() {
    setPicked(null);
    setChosen([]);
    setSearch('');
    setError(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  async function open() {
    setError(null);

    // Default to the whole household when there is exactly one of them, because that is the
    // overwhelming case and a checkbox with one option is a question nobody needs asked.
    setChosen(household.length === 1 ? [household[0].personId] : []);

    // The names a leader can enter for. Fetched when the sheet opens rather than with the panel:
    // an ask nobody is uploading against should not cost a roster read on every page load.
    if (canEnterForOthers && unitId && others === null) {
      try {
        setOthers(await requirementsApi.grid(unitId, eventId, requirementId));
      } catch {
        // A youth officer lands here with a 403, which is correct. The control simply offers
        // nothing beyond their own family rather than showing an error about a rule they cannot
        // change.
        setOthers([]);
      }
    }
  }

  async function send() {
    if (!picked || chosen.length === 0) return;

    setSending(true);
    setError(null);

    try {
      await requirementsApi.returnForm(unitId, eventId, requirementId, picked, chosen);
      close();
      await onChanged();
    } catch (e) {
      setError(
        e instanceof ValidationFailure
          ? Object.values(e.errors).flat()[0]
          : e instanceof Error
            ? e.message
            : 'That form could not be sent.',
      );
    } finally {
      setSending(false);
    }
  }

  // The leader's extra names, minus anybody already in their own household so the same person
  // cannot appear twice with two different labels.
  const mine = new Set(household.map((p) => p.personId));

  const extra = (others ?? [])
    .filter((row) => !mine.has(row.subjectPersonId))
    .filter((row) => row.displayName.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
      {forms.length > 0 && (
        <ul
          className="flex flex-wrap"
          style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--space-2)' }}
        >
          {forms.map((form) => (
            <li key={form.storedFileId}>
              <button
                type="button"
                onClick={() => onOpen(form)}
                title={`${form.requirementTitle} — ${form.subjects
                  .map((s) => s.displayName)
                  .join(' and ')}`}
                className="flex flex-col items-center"
                style={{
                  gap: 'var(--space-1)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  maxWidth: '8rem',
                }}
              >
                <img
                  src={form.thumbnailUrl ?? form.url}
                  alt={`Form returned for ${form.subjects.map((s) => s.displayName).join(' and ')}`}
                  style={{
                    display: 'block',
                    width: '4.5rem',
                    height: '4.5rem',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--color-border-subtle)',
                  }}
                />
                {/* Whose it is, under the picture. A slip covering two children says both, which
                    is the whole reason one photograph can carry more than one completion. */}
                <span
                  className="t-caption"
                  style={{ color: 'var(--color-text-secondary)', textAlign: 'center' }}
                >
                  {form.subjects.map((s) => s.displayName).join(' & ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          // No `capture`. It said `environment`, which on a phone opens the camera *instead of*
          // the picker rather than as well as it -- so a parent who had already photographed the
          // slip at the kitchen table, or been emailed a scan of it, had no way to send the copy
          // they were looking at. Without the attribute iOS and Android both offer the library and
          // the camera in one sheet, which is the superset of what `capture` gave. On a laptop
          // nothing changes; it was already being ignored there.
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setPicked(file);
              void open();
            }
          }}
        />

        <Button
          variant="secondary"
          onClick={() => fileInput.current?.click()}
          disabledReason={busy || sending ? 'One moment.' : undefined}
        >
          <Camera size={16} aria-hidden />
          {/* Not "Photograph the form" any more: the control now takes one already on the phone,
              and a verb that names only the camera is a label telling somebody the thing they
              want to do is not on offer. */}
          {forms.length > 0 ? 'Send another' : 'Send the form'}
        </Button>
      </div>

      {error && !picked && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: 0 }}
        >
          {error}
        </p>
      )}

      {picked && (
        <Overlay title="Send this form" onClose={close}>
          <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
            <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              {picked.name}
            </p>

            <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
              <p className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
                Whose form is this?
              </p>
              {/* Said outright, because it is the thing nobody expects to be allowed. A parent who
                  has written two names on one slip should not have to photograph it twice. */}
              <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                Tick everyone named on it. One form can cover more than one person.
              </p>

              {household.map((person) => (
                <CheckboxField
                  key={person.personId}
                  label={person.displayName}
                  checked={chosen.includes(person.personId)}
                  onChange={(on) =>
                    setChosen((was) =>
                      on ? [...was, person.personId] : was.filter((id) => id !== person.personId),
                    )
                  }
                />
              ))}
            </div>

            {canEnterForOthers && (others?.length ?? 0) > 0 && (
              <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
                <p
                  className="t-body-strong"
                  style={{ color: 'var(--color-text-primary)', margin: 0 }}
                >
                  Somebody else
                </p>
                {/* The on-behalf path: a leader entering the paper a family handed them at the
                    meeting. The row keeps who actually did it, so this is never mistaken for the
                    family having sent it themselves. */}
                <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                  It will show as entered by you.
                </p>

                <TextField
                  label="Find a name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Start typing"
                />

                {/* Bounded. The dev troop's roster runs to hundreds and a real one to dozens; a
                    checkbox per person is a screen nobody can use either way. */}
                {extra.slice(0, DRAWN_SUBJECTS).map((row) => (
                  <CheckboxField
                    key={row.subjectPersonId}
                    label={row.displayName}
                    checked={chosen.includes(row.subjectPersonId)}
                    onChange={(on) =>
                      setChosen((was) =>
                        on
                          ? [...was, row.subjectPersonId]
                          : was.filter((id) => id !== row.subjectPersonId),
                      )
                    }
                  />
                ))}

                {extra.length > DRAWN_SUBJECTS && (
                  <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                    {extra.length - DRAWN_SUBJECTS} more &mdash; type a name to narrow it down.
                  </p>
                )}
              </div>
            )}

            {error && (
              <p
                className="t-caption"
                role="alert"
                style={{ color: 'var(--color-status-blocked)', margin: 0 }}
              >
                {error}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => void send()}
                disabledReason={
                  sending
                    ? 'Sending...'
                    : chosen.length === 0
                      ? 'Tick whose form this is.'
                      : undefined
                }
              >
                <FileText size={16} aria-hidden />
                Send it
              </Button>
              <Button variant="secondary" onClick={close}>
                Cancel
              </Button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}
